import { Button, Checkbox, Dropdown, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { TbDotsVertical } from 'react-icons/tb'

import { formatDateUk } from '@/Ship/Helpers/fieldViews'
import { classNames } from '@/Ship/Helpers/classNames'

import { fileExtension, formatBytes, KIND_LABEL } from '../Helpers/diskFormat'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'
import { DiskNodeBadges } from './DiskNodeBadges'
import { DiskNodeIcon } from './DiskNodeIcon'
import { DiskNodePath } from './DiskNodePath'
import { DiskPerson } from './DiskPerson'
import { useDiskActions } from './useDiskActions'

import type { DragEvent, MouseEvent } from 'react'

import styles from './Disk.module.css'

/**
 * Плитка: іконка виду в превʼю-плашці, назва, розмір і хто/коли змінив;
 * чекбокс і меню — на наведенні (на телефоні — завжди). Вибір, відкриття й
 * перетягування — ті самі, що в таблиці.
 */
export const DiskNodeGrid = observer(() => {
    const actions = useDiskActions()
    // «Де лежить» — там, де рядки з різних папок: пошук і нещодавні
    const showLocation = store.isSearchActive || store.location?.kind === 'recent' || store.isSharedList

    return (
        <Spin spinning={store.isLoading || store.isSearching}>
            <div className={classNames(styles['grid'])} role="list" data-testid="disk-grid">
                {store.visibleItems.map((node) => {
                    const selected = store.isSelected(node.uuid)
                    const isFolder = node.type === 'folder'
                    const ext = isFolder ? '' : fileExtension(node.name)
                    const canDrag = node.permissions.update === true
                    const acceptsDrop = isFolder && node.permissions.write === true

                    return (
                        // Права кнопка на картці — те саме меню, що «⋯»
                        <Dropdown
                            key={node.uuid}
                            trigger={['contextMenu']}
                            menu={{ items: actions.nodeMenu(node) }}
                            onOpenChange={(open) => {
                                if (open && !selected) store.setSelected([node.uuid])
                            }}
                        >
                            <div
                                role="listitem"
                                className={classNames(
                                    styles['card'],
                                    selected && styles['cardOn'],
                                    acceptsDrop && store.dropTarget === node.uuid && styles['cardDrop']
                                )}
                                onClick={(event: MouseEvent) =>
                                    store.click(node.uuid, {
                                        ctrl: event.ctrlKey || event.metaKey,
                                        shift: event.shiftKey,
                                    })
                                }
                                onDoubleClick={() => actions.open(node)}
                                draggable={canDrag}
                                {...(canDrag
                                    ? {
                                          onDragStart: (event: DragEvent) => {
                                              store.startDrag(node)
                                              event.dataTransfer.effectAllowed = 'move'
                                              event.dataTransfer.setData('text/plain', node.name)
                                          },
                                          onDragEnd: () => store.endDrag(),
                                      }
                                    : {})}
                                {...(acceptsDrop
                                    ? {
                                          onDragOver: (event: DragEvent) => {
                                              if (!store.canDropOn(node.uuid)) return
                                              event.preventDefault()
                                              store.setDropTarget(node.uuid)
                                          },
                                          onDragLeave: () => store.setDropTarget(null),
                                          onDrop: (event: DragEvent) => {
                                              event.preventDefault()
                                              void actions.drop(node.uuid)
                                          },
                                      }
                                    : {})}
                            >
                                <span className={classNames(styles['thumb'])}>
                                    <DiskNodeIcon kind={node.kind} size={52} />
                                    {ext !== '' && <span className={classNames(styles['ext'])}>{ext}</span>}
                                </span>
                                <span className={classNames(styles['cardBody'])}>
                                    <span className={classNames(styles['nameRow'])}>
                                        <span className={classNames(styles['cardName'])} title={node.name}>
                                            {node.name}
                                        </span>
                                        <DiskNodeBadges node={node} hideShared={store.isSharedList} />
                                    </span>
                                    {showLocation && <DiskNodePath node={node} onOpen={actions.openParent} />}
                                    <span className={classNames(styles['sub'])}>
                                        {isFolder ? KIND_LABEL.folder : formatBytes(node.size)} ·{' '}
                                        {formatDateUk(node.updatedAt)}
                                    </span>
                                    <span className={classNames(styles['cardAuthor'])}>
                                        <DiskPerson user={node.createdBy} size={20} />
                                    </span>
                                </span>
                                <span
                                    className={classNames(styles['cardCheck'])}
                                    onClick={(event) => event.stopPropagation()}
                                    onDoubleClick={(event) => event.stopPropagation()}
                                >
                                    <Checkbox
                                        checked={selected}
                                        onChange={() => store.toggleSelected(node.uuid)}
                                        aria-label={`Вибрати «${node.name}»`}
                                    />
                                </span>
                                <span
                                    className={classNames(styles['cardMenu'])}
                                    onClick={(event) => event.stopPropagation()}
                                    onDoubleClick={(event) => event.stopPropagation()}
                                >
                                    <Dropdown
                                        trigger={['click']}
                                        menu={{ items: actions.nodeMenu(node) }}
                                        placement="bottomRight"
                                    >
                                        <Button
                                            size="small"
                                            icon={<TbDotsVertical />}
                                            aria-label={`Дії: ${node.name}`}
                                        />
                                    </Dropdown>
                                </span>
                            </div>
                        </Dropdown>
                    )
                })}
            </div>
        </Spin>
    )
})
